import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Eye, Globe, Monitor, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCountryName } from '@/utils/countries';

interface AnalyticsStats {
  overview: {
    totalViews: number;
    uniqueVisitors: number;
    last24h: { views: number; visitors: number };
    last7d: { views: number; visitors: number };
    last30d: { views: number; visitors: number };
  };
  topPages: Array<{ page: string; views: number }>;
  countries: Array<{ country: string; views: number }>;
  devices: Array<{ device: string; count: number }>;
  browsers: Array<{ browser: string; count: number }>;
  referrers: Array<{ referrer: string; views: number }>;
  dailyViews: Array<{ date: string; views: number; visitors: number }>;
}

const COLORS = ['#006B5F', '#00A896', '#02C39A', '#F0F3BD', '#F77F00'];

const AnalyticsDashboard = () => {
  const { getToken, isLoaded } = useAuth();
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!isLoaded) {
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);
      const apiUrl = import.meta.env.PROD
        ? '/api/admin/analytics/stats'
        : 'http://localhost:3000/api/admin/analytics/stats';

      const token = await getToken();
      if (!token) {
        setErrorMessage('Authentication is not ready yet. Please try again.');
        setStats(null);
        return;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let message = `Failed to load analytics (${response.status})`;
        try {
          const errorData = await response.json();
          if (typeof errorData?.error === 'string' && errorData.error.length > 0) {
            message = `${message}: ${errorData.error}`;
          }
        } catch {
          // Ignore non-JSON error responses
        }
        setErrorMessage(message);
        setStats(null);
        return;
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setErrorMessage('Network error while loading analytics');
      setStats(null);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, isLoaded]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    fetchAnalytics();
  }, [fetchAnalytics, isLoaded]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-gray-500">{errorMessage || 'No analytics data available'}</div>
          <Button onClick={fetchAnalytics} variant="outline" size="sm" className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#006B5F]">Analytics</h2>
          <p className="text-sm text-gray-600">Website traffic and visitor insights</p>
          {errorMessage && (
            <p className="text-sm text-amber-700 mt-1">{errorMessage}</p>
          )}
        </div>
        <Button onClick={fetchAnalytics} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Total Views
            </CardDescription>
            <CardTitle className="text-3xl">{stats.overview.totalViews.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Unique Visitors
            </CardDescription>
            <CardTitle className="text-3xl">{stats.overview.uniqueVisitors.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Last 7 Days
            </CardDescription>
            <CardTitle className="text-3xl">{stats.overview.last7d.views.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500">{stats.overview.last7d.visitors} visitors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Last 24 Hours
            </CardDescription>
            <CardTitle className="text-3xl">{stats.overview.last24h.views.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500">{stats.overview.last24h.visitors} visitors</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Views Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Views (Last 30 Days)</CardTitle>
          <CardDescription>Page views and unique visitors over time</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats.dailyViews}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="views" stroke="#006B5F" strokeWidth={2} name="Views" />
              <Line type="monotone" dataKey="visitors" stroke="#00A896" strokeWidth={2} name="Visitors" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle>Top Pages</CardTitle>
            <CardDescription>Most visited pages (last 30 days)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.topPages.slice(0, 5).map((page, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-mono text-gray-700">{page.page}</span>
                  <span className="text-sm font-semibold text-[#006B5F]">{page.views}</span>
                </div>
              ))}
              {stats.topPages.length === 0 && (
                <p className="text-sm text-gray-500">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Devices */}
        <Card>
          <CardHeader>
            <CardTitle>Devices</CardTitle>
            <CardDescription>Visitor device breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={stats.devices}
                  dataKey="count"
                  nameKey="device"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {stats.devices.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Browsers */}
        <Card>
          <CardHeader>
            <CardTitle>Browsers</CardTitle>
            <CardDescription>Top browsers (last 30 days)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.browsers.slice(0, 5)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="browser" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#006B5F" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Referrers */}
        <Card>
          <CardHeader>
            <CardTitle>Top Referrers</CardTitle>
            <CardDescription>Where visitors come from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.referrers.slice(0, 5).map((referrer, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate max-w-[200px]">{referrer.referrer}</span>
                  <span className="text-sm font-semibold text-[#006B5F]">{referrer.views}</span>
                </div>
              ))}
              {stats.referrers.length === 0 && (
                <p className="text-sm text-gray-500">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Countries */}
      {(() => {
        const realCountries = stats.countries.filter(c => c.country !== 'Unknown' && c.country !== 'Local');
        return realCountries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Top Countries</CardTitle>
              <CardDescription>Geographic distribution of visitors (last 30 days)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {realCountries.slice(0, 10).map((country, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{getCountryName(country.country)}</span>
                    <span className="text-sm font-semibold text-[#006B5F]">{country.views}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
};

export default AnalyticsDashboard;
